// Realistic 1C / KZT accounting data — one cluster × one config.
// Cluster: dd 1quart 2026 (Delta Doors, Q1 2026)
// Config: Полный СГД + расшифровки

export type RowStatus = 'match' | 'diff' | 'over' | 'none';

export interface Source {
  file: string;
  cell: string;
  value: number;
  desc: string;
}

export interface Comment {
  author: string;
  ts: string;
  text: string;
}

export interface ReportRow {
  uid: string;
  name: string;
  value: number;
  master: number | null;
  status: RowStatus;
  level: number;
  comment: Comment | null;
  sources: Source[];
  formula: string | null;
  note: string | null;
  table?: string;
  tableName?: string;
  ti?: number;
  ri?: number;
}

export interface ReportTable {
  id: string;
  num: string;
  name: string;
  meta: string;
  rows: ReportRow[];
}

export const fmt = (n: number | null | undefined): string => {
  if (n === null || n === undefined) return '—';
  const sign = n < 0 ? '−' : '';
  const abs = Math.abs(n);
  const s = abs.toString();
  const [intPart, dec] = s.split('.');
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return sign + grouped + (dec ? ',' + dec : '');
};

interface MkOpts {
  level?: number;
  comment?: Comment;
  sources?: Source[];
  formula?: string;
  note?: string;
  override?: boolean;
}

const mk = (
  name: string,
  value: number,
  master: number | null,
  opts: MkOpts = {}
): Omit<ReportRow, 'uid'> => {
  let status: RowStatus = 'match';
  if (master === null || master === undefined) status = 'none';
  else if (Math.abs(value - master) > 0.5) status = 'diff';
  if (opts.override) status = 'over';
  return {
    name,
    value,
    master,
    status,
    level: opts.level || 0,
    comment: opts.comment || null,
    sources: opts.sources || [],
    formula: opts.formula || null,
    note: opts.note || null,
  };
};

const SGD = [
  mk('Доход от реализации товаров и услуг', 1284071000, 1284071000, {
    sources: [
      { file: '7211', cell: 'E175', value: 712840471, desc: 'Реализация — стандартная ставка' },
      { file: '7211', cell: 'E176', value: 421230000, desc: 'Реализация — экспорт' },
      { file: '7211', cell: 'E177', value: 150000529, desc: 'Услуги монтажа' },
    ],
    formula: '7211!E175 + 7211!E176 + 7211!E177',
  }),
  mk('  в т.ч. реализация продукции собственного производства', 712840471, 712840471, {
    level: 1,
    sources: [{ file: '7211', cell: 'E175', value: 712840471, desc: 'Линия двери ДК-серия' }],
  }),
  mk('  в т.ч. перепродажа (импорт)', 421230000, 421230000, {
    level: 1,
    sources: [{ file: '7211', cell: 'E176', value: 421230000, desc: 'Импорт КНР, Турция' }],
  }),
  mk('  в т.ч. прочие услуги', 150000529, 150000529, { level: 1 }),

  mk('Доход от прироста стоимости', 8420000, 8420000, {
    sources: [
      { file: 'osv', cell: 'F412', value: 8420000, desc: 'Реализация ОС (склад Алматы)' },
    ],
  }),

  mk('Прочие доходы', 14820315, 14820000, {
    comment: {
      author: 'Г. Касенова', ts: '28.04 14:22',
      text: 'Расхождение на 315 тг — округление в master.xlsx. Принято.',
    },
    sources: [
      { file: 'osv', cell: 'F438', value: 12420315, desc: 'Курсовая разница' },
      { file: 'osv', cell: 'F441', value: 2400000, desc: 'Списание кредиторской задолженности' },
    ],
  }),

  mk('ИТОГО СГД', 1307311315, 1307311000, {
    level: 0,
    formula: 'sum(rows[1..3])',
    comment: {
      author: 'Г. Касенова', ts: '28.04 14:24',
      text: 'Сверила с банком — ок.',
    },
  }),

  mk('Себестоимость реализованных товаров и услуг', -842145200, -842000000, {
    comment: {
      author: 'Г. Касенова', ts: '29.04 09:11',
      text:
        'Master.xlsx устарел — Айгуль не внесла последнюю партию импорта 26 марта. ' +
        'Расхождение 145 200 тг подтверждено накладной №ИМ-0312.',
    },
    sources: [
      { file: '7111', cell: 'D210', value: -612080000, desc: 'Себестоимость продукции' },
      { file: '7111', cell: 'D211', value: -210065200, desc: 'Себестоимость импорта' },
      { file: '7111', cell: 'D212', value: -20000000, desc: 'Себестоимость услуг' },
    ],
    formula: '7111!D210 + 7111!D211 + 7111!D212',
  }),

  mk('Валовая прибыль', 465166115, 465311000, { formula: 'СГД − Себестоимость' }),

  mk('Административные расходы', -127480350, -127480350, {
    sources: [
      { file: '7470', cell: 'C44', value: -88420000, desc: 'ТБ — общая АХД' },
      { file: '7470', cell: 'C58', value: -39060350, desc: '1С — детализация' },
    ],
    formula: 'см. «Расшифровка адм. расходов»',
  }),

  mk('Расходы на реализацию', -38240000, -38240000, {
    sources: [{ file: '7470', cell: 'C72', value: -38240000, desc: 'Маркетинг + транспорт' }],
  }),

  mk('Прочие расходы', -8120000, -8120000),

  mk('Расходы по вознаграждениям', -12480000, -12480000, {
    override: true,
    note: 'NOTE: ручная корректировка по записке от 12.04 — начисление за Q4 2025 переотнесено',
    sources: [{ file: 'osv', cell: 'F511', value: -12480000, desc: 'Проценты по займам' }],
  }),

  mk('Прибыль до налогообложения', 278845765, 278990650, {
    formula: 'Валовая прибыль − ОПЕРРАСХОДЫ',
  }),

  mk('Корпоративный подоходный налог (КПН) 20%', -55769153, -55798130, {
    comment: {
      author: 'Г. Касенова', ts: '29.04 11:40',
      text: 'Пересчитать после уточнения адм. расходов. До конца недели.',
    },
  }),

  mk('Чистая прибыль', 223076612, 223192520, { level: 0 }),
];

const ADM_TB = [
  mk('Заработная плата АУП', -42180000, -42180000, {
    sources: [
      { file: '7470', cell: 'C12', value: -38420000, desc: 'Оклады' },
      { file: '7470', cell: 'C13', value: -3760000, desc: 'Премии Q1' },
    ],
  }),
  mk('Социальный налог и отчисления', -8230000, -8230000),
  mk('Аренда офиса (Алматы, пр. Абая 150)', -12600000, -12600000, {
    sources: [{ file: '7470', cell: 'C18', value: -12600000, desc: '4 200 000 × 3 мес' }],
  }),
  mk('Коммунальные платежи', -1840000, -1820000, {
    comment: {
      author: 'Г. Касенова', ts: '27.04 10:05',
      text: 'Включён март — счёт пришёл 25 апреля.',
    },
  }),
  mk('Связь и интернет', -480000, -480000),
  mk('Канцелярия и расходные материалы', -1240000, -1240000),
  mk('Командировочные', -3820000, -3820000),
  mk('Юридические и консультационные услуги', -8400000, -8400000),
  mk('Аудиторские услуги', -2500000, -2500000),
  mk('Программное обеспечение и подписки (1С, Office)', -1840000, -1840000),
  mk('Банковские услуги', -680000, -680000),
  mk('Представительские расходы', -2410000, -2410000),
  mk('Амортизация ОС административного назначения', -2200000, -2200000, {
    override: true,
    note: 'NOTE: метод линейный, согласовано с УКТУ 2025-12-15',
  }),
  mk('ИТОГО (ТБ)', -88420000, -88400000, { level: 0, formula: 'sum(rows[1..13])' }),
];

const ADM_1C = [
  mk('Заработная плата (1С: ЗУП выгрузка)', -42180000, -42180000, {
    sources: [{ file: 'sole_7470', cell: 'B5', value: -42180000, desc: 'Свод ФОТ Q1 2026' }],
  }),
  mk('Налоги с ФОТ (ИПН, ОПВ, ВОСМС)', -8230000, -8230000),
  mk('Аренда (счёт 7110)', -12600000, -12600000),
  mk('Коммунальные (счёт 7111)', -1840000, -1820000, {
    comment: { author: 'Г. Касенова', ts: '27.04 10:06', text: 'См. ТБ — то же расхождение.' },
  }),
  mk('Связь', -480000, -480000),
  mk('Канцтовары', -1240000, -1240000),
  mk('Командировки', -3820000, -3820000),
  mk('Услуги сторонних организаций', -10900000, -10900000, {
    sources: [
      { file: 'osv', cell: 'F622', value: -8400000, desc: 'Юр. услуги' },
      { file: 'osv', cell: 'F623', value: -2500000, desc: 'Аудит' },
    ],
  }),
  mk('ПО и подписки', -1840000, -1840000),
  mk('Банк', -680000, -680000),
  mk('Представительские', -2410000, -2410000),
  mk('Амортизация', -2200000, -2200000, {
    override: true,
    note: 'NOTE: тот же метод что и в ТБ',
  }),
  mk('Прочие расходы общехозяйственного назначения', -540350, 0, {
    comment: {
      author: 'Г. Касенова', ts: '29.04 12:30',
      text: 'В master не было — добавить отдельной строкой. Подтверждение: акт №АХД-018.',
    },
    sources: [{ file: 'osv', cell: 'F699', value: -540350, desc: 'Уборка, мелкий ремонт' }],
  }),
  mk('ИТОГО (1С)', -88960350, -88420000, { level: 0, formula: 'sum(rows[1..13])' }),
];

export const TABLES: ReportTable[] = [
  { id: 'sgd',    num: '01', name: 'СГД (главное)',                          meta: 'основной отчёт',         rows: [] },
  { id: 'adm_tb', num: '02', name: 'Расшифровка адм. расходов · ТБ',          meta: 'ист.: 7470',             rows: [] },
  { id: 'adm_1c', num: '03', name: 'Расшифровка адм. расходов · 1С',          meta: 'ист.: sole_7470 / osv',  rows: [] },
];

const RAW = [SGD, ADM_TB, ADM_1C];
TABLES.forEach((t, ti) => {
  t.rows = RAW[ti].map((r, ri) => ({
    ...r,
    uid: `${t.id}-${ri}`,
    table: t.id,
    tableName: t.name,
    ti,
    ri,
  }));
});

export const ALL_ROWS: ReportRow[] = TABLES.flatMap((t) => t.rows);

export const COUNTS = ALL_ROWS.reduce(
  (acc: Record<RowStatus, number>, r) => {
    acc[r.status] = (acc[r.status] || 0) + 1;
    return acc;
  },
  { match: 0, diff: 0, over: 0, none: 0 }
);
